/* eslint-disable no-self-assign */
/* eslint-disable guard-for-in */
import { deepClone } from './util/deepClone';

import canvasEnums from './enums/canvas';
import labelerEnums from './enums/labeler';
import {
	renderDuplicateLabelsCategories,
	showMarketCloseLabelsCategories
} from './enums/modcharts';
import defaultLabelTemplates from './locale/defaultLabelTemplates';
import defaultRowTemplateThresholds from './locale/defaultRowTemplateThresholds';
import subpixel from './util/canvasSubpixelOffset';

const { labelPriorities } = labelerEnums;

/** Performs these actions on a ruler: create labels, align labels, position labels, render labels */
class Labeler {
	constructor(axis, core) {
		this.axis = axis;
		this.core = core;
		this.labels = {};
		this.rowTemplate = {};
	}

	/** Returns a formatted string for a given date */
	format(date, labelType, formatType = 'default') {
		const { core } = this;
		let { labelTemplates } = core.params.locale;

		if (!labelTemplates) {
			// labelTemplates could be missing if loading a saved chart from before this feature was added
			core.warn("'labelTemplates' not found in locale. Using 'defaultLabelTemplates' instead.");
			core.params.locale.labelTemplates = defaultLabelTemplates;
			labelTemplates = defaultLabelTemplates;
		}

		// Determine the format key based on single-row mode and intraday state
		const isSingleRowMode = this.core.params.xAxisSingleRowMode;
		const isIntraday = core.state.isIntraday;
		const formatKey = (isSingleRowMode && isIntraday) ? 'singleRow' : 'doubleRow';

		// Ensure the labelType exists in the selected format key
		let labelTypeTemplates = labelTemplates.xAxis[formatKey][labelType];
		if (!labelTypeTemplates) {
			// core.warn(`'${labelType}' not found in '${formatKey}'. Using 'doubleRow' instead.`);
			labelTypeTemplates = labelTemplates.xAxis.doubleRow[labelType];
		}

		// Ensure the formatType exists within the labelTypeTemplates
		const dateFormat = labelTypeTemplates[formatType];
		if (!dateFormat) {
			// core.warn(`'${formatType}' not found in '${labelType}' for '${formatKey}'. Using 'default' instead.`);
			return core.timeFormat(labelTypeTemplates.default)(date);
		}

		return core.timeFormat(dateFormat)(date);
	}

	/**
	 * Returns a dynamic step based on the distance between labels of a certain type
	 * (weekly, monthly, etc.). This works by iterating through available label steps
	 * until the distance provided exceeds the spacing value. For example: Say we
	 * know the distance between the January and February labels is 48.5px, and we've
	 * defined a spacing value of 50 for "months" labels. It will begin by looking at
	 * the array of steps for months ([1, 2, 3, 4, 6]).  Since 48.5 is less than 50/1
	 * it will move to the next step and repeat.  48.5 is not less than 50/2, so it
	 * will stop iterating there and return a step of 2.
	 */
	_step(dist, spacing, steps) {
		return steps.find(step => dist >= spacing / step) || 1;
	}

	/**
	 * Generate a full collection of fresh labels for the entire XAxis. Each label
	 * will contain information about itself: its date, widths, formatted strings
	 * and priority. Only a subset of these labels will be chosen during XAxis
	 * renders, e.g. if the entire array has 8500 "days" labels, a given render
	 * may only be processing 19 labels. This method typically needs to be called on
	 * first load, or if the locale language was changed, or if switching between
	 * interday/intraday.
	 */
	_createLabels() {
		const { core } = this;
		const { exchangeDates } = core;
		const years = [];
		const months = [];
		const weeks = [];
		const days = [];
		const hours = [];
		const minutes = [];
		const isIntraday =
				typeof core.state.isIntraday === 'boolean'
					? core.state.isIntraday
					: core.params.days <= core.limitIntradayTrading;
		const denseLabelLimitLeft = new Date().setDate(
			new Date().getDate() - core.limitIntradayTrading - 10
		);
		const denseLabelLimitRight = new Date().setDate(new Date().getDate() + 4);
		let lastYear;
		let lastMonth;
		let lastWeek;
		let lastDay;
		let lastHour;
		let lastMin;
		let date;
		let dateLabel;
		let year;
		let month;
		let week;
		let day;
		let hour;
		let min;

		for (let i = 0; i < exchangeDates.length; i++) {
			date = new Date(exchangeDates[i]);
			dateLabel = new Date(date);

			year = date.getFullYear();
			month = date.getMonth();
			week = this.getWeeks(date);
			day = date.getDate();
			hour = date.getHours();
			min = date.getMinutes();

			/**
			 * 'first' labels represent what will be displayed in the bottom row if the
			 * given label is in the first position in the top row (furthest to the left).
			 * This generally should match the default format of the next bigger timeframe.
			 * 'firstAlt' labels are used when additional information needs to be added
			 * to the label to specify the time period (eg. adding the year to January
			 * labels when the bottom row shows month names).
			 */
			if (year !== lastYear) {
				years.push({
					index: i,
					date,
					format: {
						default: this.format(date, 'years'),
						first: this.format(date, 'years', 'first')
					},
					stepCompare: date.getFullYear(),
					priority: labelPriorities.LOW
				});

				lastYear = year;
			}

			if (month !== lastMonth) {
				months.push({
					index: i,
					date,
					format: {
						default: this.format(date, 'months'),
						first: this.format(date, 'months', 'first'),
						firstAlt: this.format(date, 'months', 'firstAlt')
					},
					stepCompare: date.getMonth(),
					pairedFirst: 'years',
					priority: labelPriorities.LOW
				});

				lastMonth = month;
			}

			if (week !== lastWeek) {
				weeks.push({
					index: i,
					date,
					format: {
						default: this.format(date, 'weeks'),
						first: this.format(date, 'weeks', 'first'),
						firstAlt: this.format(date, 'weeks', 'firstAlt')
					},
					stepCompare: this.getWeeks(date),
					pairedFirst: 'months',
					priority: labelPriorities.LOW
				});

				lastWeek = week;
			}

			if (day !== lastDay) {
				days.push({
					index: i,
					date,
					format: {
						default: this.format(date, 'days'),
						first: this.format(date, 'days', 'first'),
						firstAlt: this.format(date, 'days', 'firstAlt'),
						intraday: this.format(date, 'days', 'intraday')
					},
					stepCompare: date.getDate(),
					pairedFirst: 'months',
					priority: labelPriorities.LOW
				});

				lastDay = day;
			}

			if (isIntraday) {
				const minutesFromStartOfDay = date.getHours() * 60 + date.getMinutes();

				// Get default market close/open times
				let previousClose = core.state.typicalSessions
					? core.state.typicalSessions.max
					: null;
				let currentOpen = core.state.typicalSessions
					? core.state.typicalSessions.min
					: null;

				// Get date from previous element or use today's date
				let previousTradingDate = new Date();
				if (i > 0) previousTradingDate = new Date(exchangeDates[i - 1]);

				// Function to get alternate session data, if the dates match
				const checkForAlternateSession = dateToCheck => {
					// Get the dates (which are the keys)
					const alternateSessionDates = Object.keys(core.timeService.alternateSessions);

					// Look for a matching date
					const matchingDate = alternateSessionDates.find(dateString => {
						const dateMatch = new Date(dateString);

						return (
							dateToCheck.getYear() === dateMatch.getYear() &&
							dateToCheck.getMonth() === dateMatch.getMonth() &&
							dateToCheck.getDate() === dateMatch.getDate()
						);
					});

					// Look for a matching exchange ID
					return (
						matchingDate &&
						core.timeService.alternateSessions[matchingDate]
							.find(session => session.exchangeId === core.xref[core.params.symbol].exchangeId)
					);
				};

				// Check for alternate sessions associated with PREVIOUS trading date
				if (
					core.timeService.alternateSessions && // Are there alternate sessions?
					checkForAlternateSession(previousTradingDate)
				) {
					// Get the open/close info for that session
					const session = checkForAlternateSession(previousTradingDate);
					previousClose = session.close;
				}

				// Check for alternate sessions associated with CURRENT trading date
				if (
					core.timeService.alternateSessions &&
					checkForAlternateSession(date)
				) {
					// Get the open/close info for that session
					const session = checkForAlternateSession(date);
					currentOpen = session.open;
				}

				// Check if time from current data point is for market open
				const isMarketOpen = currentOpen === minutesFromStartOfDay;

				/**
				 * If session start/end data exists, add market close label BEFORE next market
				 * open label (if flag is enabled)
				 * */
				if (
					core.params.showMarketCloseLabels &&
					isMarketOpen
				) {
					const marketClose = {
						hours: Math.floor(previousClose / 60),
						minutes: previousClose % 60
					};

					/**
					 * CH-1513
					 * If market close label is for current date, it gets a higher priority such that it draws
					 * instead of the (future) market open label. We have to look at the next day because the
					 * market close label lives at the same time as the market open label, which in this case
					 * is on the next day.
					*/
					const datePriorityCheck = new Date((new Date()).setDate(new Date().getDate() + 1));

					// Set date to use market close time
					previousTradingDate.setHours(marketClose.hours);
					previousTradingDate.setMinutes(marketClose.minutes);

					hours.push({
						index: i,
						date,
						format: {
							default: this.format(previousTradingDate, 'hours'),
							// Should this be date instead of previousTradingDate?
							first: this.format(date, 'hours', 'first')
						},
						stepCompare: date.getHours(),
						pairedFirst: 'days',
						// Use toLocaleDateString to avoid time check
						priority: date.toLocaleDateString() === datePriorityCheck.toLocaleDateString()
							? labelPriorities.HIGHEST
							: labelPriorities.MARKET_CLOSE,
						isMarketClose: true
					});

					minutes.push({
						index: i,
						date,
						format: {
							default: this.format(previousTradingDate, 'minutes'),
							// Should this be date instead of previousTradingDate?
							first: this.format(date, 'minutes', 'first')
						},
						stepCompare: date.getMinutes(),
						pairedFirst: 'days',
						// Use toLocaleDateString to avoid time check
						priority: date.toLocaleDateString() === datePriorityCheck.toLocaleDateString()
							? labelPriorities.HIGHEST
							: labelPriorities.MARKET_CLOSE,
						isMarketClose: true
					});
				}

				// If within dense label limits, add hour and minute
				if (
					date > denseLabelLimitLeft &&
					date < denseLabelLimitRight
				) {
					// Check time to add correct priority
					const priority = isMarketOpen
						? labelPriorities.MARKET_OPEN
						: date.getHours() === 12 && date.getMinutes() === 0
							? labelPriorities.MIDDAY
							: labelPriorities.LOW;

					if (hour !== lastHour) {
						hours.push({
							index: i,
							date,
							format: {
								default: this.format(dateLabel, 'hours'),
								first: this.format(dateLabel, 'hours', 'first')
							},
							stepCompare: date.getHours(),
							pairedFirst: 'days',
							priority,
							isMarketOpen
						});

						lastHour = hour;
					}

					if (min !== lastMin) {
						minutes.push({
							index: i,
							date,
							format: {
								default: this.format(dateLabel, 'minutes'),
								first: this.format(dateLabel, 'minutes', 'first')
							},
							stepCompare: date.getMinutes(),
							pairedFirst: 'days',
							priority,
							isMarketOpen
						});

						lastMin = min;
					}
				}
			}
		}

		this.labels = this.measureLabels({
			minutes,
			hours,
			days,
			weeks,
			months,
			years
		});
	}

	/**
	 * Measures and stores label widths for future collision detection logic
	 * @param {object} labels - Label object to use in calculations
	 * @return {object} Label object with measurements added
	 * */
	measureLabels(labels) {
		const { panel } = this.axis;
		const ctx = panel.rootContext;
		const { style } = panel.params;

		// before measuring label dimensions, set to axis label style
		ctx.font = panel.getFontStyle(
			style.xAxis.font.weight,
			style.xAxis.font.size,
			style.xAxis.font.family
		);
		Object.keys(labels).forEach(label => {
			const vals = labels[label];
			vals.forEach(value => {
				value.width = {};
				const { format, width } = value;

				Object.keys(format).forEach(formatType => {
					const formatValue = format[formatType];
					width[formatType] = ctx.measureText(formatValue).width;
				});
			});
		});

		return labels;
	}

	/**
	 * Returns the number of non-weekend days between two dates
	 * @param {Date} startDate
	 * @param {Date} endDate
	 * @return {number}
	 */
	getWeekdays(startDate, endDate) {
		let weekDay;
		let num = 0;
		const currentDate = new Date(startDate);

		while (currentDate <= endDate) {
			weekDay = currentDate.getDay();

			if (weekDay !== 0 && weekDay !== 6) {
				num++;
			}

			currentDate.setDate(currentDate.getDate() + 1);
		}

		return num;
	}

	/**
	 * Returns the numeric week of the year
	 * @param {Date} date - UTC date
	 * @return {number}
	 */
	getWeeks(date) {
		const jan = new Date(date.getFullYear(), 0, 1);
		return Math.ceil(((date - jan) / 86400000 + jan.getDay() + 1) / 7);
	}

	/**
	 * given current xScale domain, trim labels collection to just the appropriate labels
	 * @return {object}
	 */
	filterLabels() {
		const self = this;
		const { panel } = this.axis;
		const { core } = this;
		const ruler = core.exchangeDates;
		const domain = this.axis.primaryScale.domain();
		const domainLeft = Math.max(0, Math.floor(domain[0]));
		const domainRight = Math.min(ruler.length - 1, Math.floor(domain[1]));
		let i;
		let dist;
		let lastX;
		let rowLabel;
		const labels = {};
		let idxLeft;
		let idxRight;
		let step;
		let newLabels;
		const loopLabels = {};
		let count;
		let amt;
		const style = panel.params.style.xAxis;

		this.rowTemplate = this._getRowTemplate(domainLeft, domainRight);

		loopLabels[this.rowTemplate.upper] = self.labels[this.rowTemplate.upper];

		if (this.rowTemplate.lower) {
			loopLabels[this.rowTemplate.lower] = self.labels[this.rowTemplate.lower];
		}

		// trim all labels down to what's in current range
		Object.keys(loopLabels).forEach(rowId => {
			const rowLabels = loopLabels[rowId];
			if (!rowLabels || !rowLabels.length) return;

			const labelSteps = style.label.steps[rowId];
			const labelSpacing = style.label.spacing[rowId] || style.label.spacing.default;

			idxLeft = Math.max(
				0,
				core._getIndexByDomain(domainLeft, rowLabels) - 1
			);
			idxRight = Math.min(
				rowLabels.length - 1,
				core._getIndexByDomain(domainRight, rowLabels) + 1
			);

			labels[rowId] = rowLabels.slice(idxLeft, idxRight + 1);

			// calculate x positions of all rows and max dist
			count = 0;
			amt = 0;

			lastX = false;

			// loop backward so we can splice
			for (i = labels[rowId].length - 1; i >= 0; i--) {
				rowLabel = labels[rowId][i];
				rowLabel.x0 = panel.xAxis.primaryScale(rowLabel.index);

				// If label is market close, add negative subpixel offset
				if (
					rowLabel.isMarketClose &&
					rowLabel.x0 >= panel.size.padding.left + panel.size.width // Check right chart edge
				) {
					rowLabel.x0 = subpixel.px(rowLabel.x0, false);
				}

				// trim if outside of chart boundaries
				if (
					rowLabel.x0 < panel.size.padding.left || // Check left chart edge
					rowLabel.x0 > panel.size.padding.left + panel.size.width // Check right chart edge
				) {
					labels[rowId].splice(i, 1);
				} else if (
					lastX &&
					// Don't include label 0 since its dist is often much shorter and throws off the step calculation
					i > 0 &&
					// Don't add market close labels (because they're a special case and could throw off the calculation)
					!rowLabel.isMarketClose
				) {
					amt += lastX - rowLabel.x0;
					count++;
				}

				lastX = rowLabel.x0;
			}

			// find average distance between all distances deemed valid
			dist = amt / count;

			// trim out row if dist is too small
			if (dist < 0.1) {
				labels[rowId] = null;
				return true;
			}

			// now figure out a good step
			step = self._step(dist, labelSpacing, labelSteps);

			// Trim vals in each row that don't match the step, unless they're high priority
			newLabels = labels[rowId].filter(row => {
				return (
					row.priority <= labelPriorities.HIGH || // Don't filter out high priority labels
					row.stepCompare % step === 0
				);
			});

			if (rowId === 'hours') {
				newLabels.forEach(row => {
					row.step = step;
				});
			}

			labels[rowId] = newLabels;

			// other removals

			// trim out years if we have days and months
			if (
				rowId === 'years' &&
				labels.days &&
				labels.days.length &&
				labels.months &&
				labels.months.length
			) {
				labels.years = null;
				return true;
			}

			// trim out hours if we have days and minutes
			if (rowId === 'hours' && labels.minutes && labels.minutes.length) {
				labels.hours = null;
				return true;
			}

			// trim out weeks if we have days
			if (rowId === 'weeks' && labels.days && labels.days.length) {
				labels.weeks = null;
				return true;
			}
		});

		return labels;
	}

	/**
	 * Determines the appropriate upper/lower xAxis row template id's.
	 * Example: an interday chart may return "months" and "years", but an intraday chart may return
	 * "hours" and "days". Depending on the width of the chart and other factors, different templates
	 * will be returned.
	 * @param {number} rulerLeft - The index for the left side of the xAxis domain
	 * @param {number} rulerRight - The index for the right side of the xAxis domain
	 * @returns {object} The selected row template
	 */
	_getRowTemplate(rulerLeft, rulerRight) {
		const { core } = this;
		const { width } = this.axis.panel.size;

		let { rowTemplateThresholds } = core.params.locale;

		// rowTemplateThresholds could be missing or malformed if loading a saved chart from before this feature was added
		if (
			!rowTemplateThresholds ||
			rowTemplateThresholds.find(row => row.width === null)
		) {
			core.warn("'rowTemplateThresholds' not found in locale. Using 'defaultRowTemplateThresholds' instead.");
			core.params.locale.rowTemplateThresholds = defaultRowTemplateThresholds;
			rowTemplateThresholds = defaultRowTemplateThresholds;
		}
		const chartType = core.state.isIntraday
			? 'intraday'
			: 'interday';

		// Figure out what time span comparison should be (value in hours for intraday or days for interday)
		const ruler = core.exchangeDates;
		const timeSpan = chartType === 'intraday'
			? (rulerRight - rulerLeft) / 60
			: core.params.customData
				? Math.ceil(((ruler[rulerRight] - ruler[rulerLeft]) / (1000 * 60 * 60 * 24)) * 0.69)
				: rulerRight - rulerLeft;

		// Find correct row template
		const { lower, upper } = rowTemplateThresholds
			// CH-1823: We might need to add sorting as a CYA operation
			.find(widthThreshold => width <= widthThreshold.width)[chartType]
			.find(timeSpanThreshold => timeSpan <= timeSpanThreshold.timeSpan);

		return { lower,	upper };
	}

	/**
	 * Renders each row (years, months, days, etc.) of labels on the X-axis.
	 *
	 * This method iterates through the provided labels, applying specific rendering rules
	 * based on whether the chart is intraday or interday. It handles right-alignment for the
	 * final visible label, ensures labels don't overlap, and applies optional styling for the
	 * bottom row. The method also manages label rendering based on whether they fall within
	 * the visible boundaries of the X-axis.
	 *
	 * Key functionalities:
	 * - Handles both intraday and interday charts differently for final label alignment.
	 * - Allows overlap of labels up to a specified limit (`overlap.right`).
	 * - Applies different styles for the bottom row of labels if specified.
	 * - Ensures labels are only drawn if they do not collide with neighboring labels or exceed boundaries.
	 *
	 * @param {Object} labels - An object containing the labels to be rendered, grouped by their respective rows (e.g., years, months).
	 */
	render(labels) {
		const { panel } = this.axis;
		const ctx = panel.rootContext;
		const { style } = panel.params;
		const labelsRendered = {};

		// Calculate the starting Y position for labels and set padding values
		const y0 = panel.size.height + panel.size.padding.top + (style.xAxis.label.padding.top || 0);
		const paddingLeft = style.xAxis.label.padding.left;
		const paddingRight = style.xAxis.label.padding.right;

		// Set the left and right limits based on the X-axis padding and width
		const limitLeft = panel.size.padding.left;
		const limitRight = panel.size.padding.left + panel.size.width;

		let label = {};
		let rowNum = 0; // Row counter for determining label positions vertically
		let newY = 0; // Y-coordinate for label rendering
		let i = 0; // General loop counter
		let left = 0; // Left boundary of the current label
		let right = 0; // Right boundary of the current label

		// Prepare the canvas for drawing
		ctx.beginPath();
		ctx.textBaseline = 'top';

		// Apply any specific rendering rules to the labels before starting the drawing process
		labels = this.applyRenderLabelRules(labels);

		// Set the initial text color and font style based on the configuration
		ctx.fillStyle = style.xAxis.label.color;
		ctx.font = panel.getFontStyle(
			style.xAxis.font.weight,
			style.xAxis.font.size,
			style.xAxis.font.family
		);

		const isIntraday = panel.core.state.isIntraday; // Determine if the chart is intraday
		let finalVisibleLabelIndex = -1; // Index of the last visible label on the X-axis

		for (const row in labels) {
			const rowLabels = labels[row];
			let previousLeft = limitRight; // Initialize the left boundary of the previous label

			// Apply different styles for the bottom row of labels if this is not the first row
			if (rowNum > 0) {
				ctx.fillStyle = style.xAxis.bottomRow.label.color || style.xAxis.label.color;
				ctx.font = panel.getFontStyle(
					style.xAxis.bottomRow.font.weight || style.xAxis.font.weight,
					style.xAxis.bottomRow.font.size || style.xAxis.font.size,
					style.xAxis.bottomRow.font.family || style.xAxis.font.family
				);
			}

			// Identify the final visible label within the right limit, ensuring it belongs to the current session
			for (i = rowLabels.length - 1; i >= 0; i--) {
				if (rowLabels[i].x0 <= limitRight) {
					if (isIntraday && rowLabels[i].isMarketClose) {
						finalVisibleLabelIndex = i;
						break;
					} else if (!isIntraday) {
						finalVisibleLabelIndex = i;
						break;
					}
				}
			}

			for (i = rowLabels.length - 1; i >= 0; i--) {
				label = rowLabels[i];
				let labelCoord;
				let rightCollide = false; // Flag to check if the label collides with its right-hand neighbor
				const labelFormat = label.format;
				let textAlign = style.xAxis.label.textAlign || canvasEnums.textAlign.LEFT;

				const overlapRight = style.xAxis.label.overlap.right; // Pixel value

				// Special case for the final label in intraday charts (right-align it if necessary)
				if (
					isIntraday &&
					this.core.params.showMarketCloseLabels === 'once' &&
					i === finalVisibleLabelIndex
				) {
					if ((label.x0 + label.width.default - limitRight) <= overlapRight) {
						// Allow the label to overlap slightly beyond the X-axis boundary
						labelCoord = label.x0;
					} else {
						// Right-align the final label if overlap is too large
						textAlign = canvasEnums.textAlign.RIGHT;
						labelCoord = limitRight - paddingRight;
					}
				} else if (
					!isIntraday &&
					i === finalVisibleLabelIndex && // Only apply to the final label
					label.x0 + label.width.default > limitRight // Label would normally be outside the X axis
				) {
					const previousLabel = rowLabels[i - 1];

					if (this.core.params.enableRightAlignFinalLabel) {
						if ((label.x0 + label.width.default - limitRight) <= overlapRight) {
							// Allow the label to overlap slightly beyond the X-axis boundary
							labelCoord = label.x0;
						} else if (
							!previousLabel ||
								(previousLabel &&
								(previousLabel.x0 + previousLabel.width.default + paddingLeft) <
								(limitRight - paddingRight - label.width.default))
						) {
							// Right-align the label if it avoids collision and overlap is too large
							textAlign = canvasEnums.textAlign.RIGHT;
							labelCoord = limitRight - paddingRight; // Adjusted to subtract paddingRight
						} else {
							// Skip rendering if it would collide with the previous label
							continue;
						}
					} else if ((label.x0 + label.width.default - limitRight) <= overlapRight) {
						// Allow the label to overlap slightly beyond the X-axis boundary
						labelCoord = label.x0;
					} else {
						// Skip rendering if it exceeds the right limit and no special logic applies
						continue;
					}
				} else if (label.x0 + label.width.default > limitRight) {
					// Skip rendering if the label exceeds the X-axis width and no special logic applies
					continue;
				} else {
					// Default case: keep the label's original X position
					labelCoord = label.x0;
				}

				ctx.textAlign = textAlign;

				// Determine the left and right boundaries of the label based on its alignment
				switch (textAlign) {
					case canvasEnums.textAlign.RIGHT:
						right = labelCoord;
						left = right - label.width.default;
						break;
					case canvasEnums.textAlign.CENTER:
						left = label.x0 - (label.width.default / 2);
						right = left + label.width.default;
						labelCoord = label.x0;
						break;
					case canvasEnums.textAlign.LEFT:
					default:
						left = label.x0 + paddingLeft;
						right = left + label.width.default;
						labelCoord = left;
				}

				// Check if the current label collides with the right-hand neighbor
				rightCollide =
					i < rowLabels.length - 1 && // Only check for collision if there's a next label
					right >= previousLeft;

				// Draw the label if it doesn't collide with its neighbor and is within the left boundary
				if (
					!rightCollide &&
					left > limitLeft
				) {
					// Calculate the Y position for the label and draw it on the canvas
					newY = y0 + rowNum * (style.xAxis.label.lineHeight || style.xAxis.font.size + 4);
					ctx.fillText(labelFormat.default, labelCoord, newY);

					if (!labelsRendered[row]) {
						labelsRendered[row] = [];
					}
					// save rendered labels for unit tests
					labelsRendered[row].push({
						text: labelFormat.default,
						x: labelCoord,
						y: newY,
						textAlign,
						label: { ...label },
						isOverlap: textAlign === 'left' ? labelCoord + label.width.default > limitRight : labelCoord > limitRight
					});

					// Update the left boundary for the next label
					previousLeft = left;
				}
			}

			if (rowLabels.length) rowNum++;
		}
		return labelsRendered;
	}

	/**
	 * Checks if a label is a duplicate based on the given params.renderDuplicateLabels setting. (CH-1748)
	 * This helps in determining which labels to keep or discard when there are duplicate labels.
	 * Normally duplicates won't occur, but certain combinations of user-defined format templates can sometimes cause them to happen.
	 * Note: KEEP_RIGHT is wonky right now and will need further work to account for the dynamically inserted left labels.
	 *
	 * @param {Object} label - The current label being checked.
	 * @param {Array} rowLabels - The array of labels in the current row.
	 * @param {number} i - The index of the current label in the rowLabels array.
	 * @return {boolean} - Returns true if the label is a duplicate and should be discarded, false otherwise.
	 */
	isDuplicateLabel(label, rowLabels, i) {
		let isDuplicate = false;

		if (this.core.params.renderDuplicateLabels !== renderDuplicateLabelsCategories.KEEP_ALL) {
			const previousLabel = i < rowLabels.length - 1 ? rowLabels[i + 1] : null;
			const nextLabel = i > 0 ? rowLabels[i - 1] : null;

			switch (this.core.params.renderDuplicateLabels) {
				case renderDuplicateLabelsCategories.KEEP_LEFT:
					if (nextLabel && nextLabel.format.default === label.format.default) {
						isDuplicate = true;
					}
					break;
				case renderDuplicateLabelsCategories.KEEP_RIGHT:
					if (previousLabel && previousLabel.format.default === label.format.default) {
						isDuplicate = true;
					}
					break;
				default:
					break;
			}
		}

		return isDuplicate;
	}

	/** Just before rendering, modify specific labels based on rules */
	applyRenderLabelRules(labels) {
	// Check if the Labeler is in single-row mode
		if (this.core.params.xAxisSingleRowMode) {
			const lowerRow = this.rowTemplate.lower;
			const upperRow = this.rowTemplate.upper;

			if (labels[upperRow] && labels[lowerRow]) {
				const upperLabels = labels[upperRow];
				const lowerLabels = labels[lowerRow];

				// Merge the lower row values into the upper row values
				lowerLabels.forEach(lowerLabel => {
					const upperIndex = upperLabels.findIndex(label => label.index === lowerLabel.index);

					if (upperIndex !== -1) {
						// Replace the upper row label with the lower row label if it fits
						if (!this.replaceIfFits(upperLabels[upperIndex], lowerLabel, true)) {
							// If none of the formats fit, keep the original upper label
							upperLabels[upperIndex] = upperLabels[upperIndex];
						}
					} else {
						// If no corresponding upper label, add the lower label to the upper row
						upperLabels.push(lowerLabel);
					}
				});

				// Sort the upper row labels to maintain order
				upperLabels.sort((a, b) => a.index - b.index);

				// Delete the lower row
				delete labels[lowerRow];
			}
		}

		// Apply bottom row first label formatting (usually to ensure the year is always visible)
		for (const labelType in labels) {
			const labelTypeCollection = labels[labelType];
			if (
				labelTypeCollection.length &&
					labelTypeCollection[0].format &&
					labelType !== 'years'
			) {
				labels = this.updatePairedLabelRow(labelTypeCollection[0], labels, this.core.params.xAxisSingleRowMode ? this.rowTemplate.upper : this.rowTemplate.lower);
				break;
			}
		}

		// Apply special case rules by overwriting the default format and width properties for matching labels
		let rowNum = 0;
		for (const labelType in labels) {
			const labelTypeCollection = labels[labelType];

			// Work from right to left
			for (let i = labelTypeCollection.length - 1; i >= 0; i--) {
				const label = labelTypeCollection[i];
				const labelFormat = label.format;

				// Rule: bottom row intraday labels use "intraday" formatting, only for when the top row is minutes or hours
				// CH-1615: This is a bit of an ugly rule and should probably be removed in the future. But it works for now.
				if (
					labelFormat.intraday &&
						i > 0 &&
						(
							(labels.hours && labels.hours.length) ||
								(labels.minutes && labels.minutes.length)
						)
				) {
					labelFormat.default = labelFormat.intraday;
					label.width.default = label.width.intraday;
				}

				// Rule: add year to January labels when bottom row is "months" (e.g., use the "firstAlt" formatting)
				if (
					labelType === 'months' &&
						rowNum > 0 &&
						label.date.getMonth() === 0 &&
						!this.core.params.xAxisSingleRowMode
				) {
					labelFormat.default = labelFormat.firstAlt;
					label.width.default = label.width.firstAlt;
				}
			}

			// Increment row counter to allow for format check on months row
			if (labelTypeCollection.length) rowNum++;
		}

		// Special case: Ensure consistency in intraday single-row mode
		if (this.core.params.xAxisSingleRowMode && this.core.state.isIntraday) {
			const upperRow = this.rowTemplate.upper;
			const upperLabels = labels[upperRow];

			// Ensure first label of the day uses the date format
			if (upperLabels && upperLabels.length > 1) {
				for (let i = 0; i < upperLabels.length; i++) {
					const label = upperLabels[i];
					const previousLabel = i > 0 ? upperLabels[i - 1] : null;
					const labelDate = new Date(label.date);

					// Check if it's the first label of the day
					if (i === 0 || (previousLabel && new Date(previousLabel.date).getDate() !== labelDate.getDate())) {
						label.format.default = label.format.first;
						label.width.default = label.width.first;
					} else {
						label.format.default = label.format.default;
						label.width.default = label.width.default;
					}
				}
			}
		}

		return labels;
	}

	/**
	* Replace the target label with the new label if it fits within the available width.
	* Try different format options (first, default) if the initial choice does not fit.
	* @param {Object} targetLabel - The label to be replaced.
	* @param {Object} newLabel - The new label to use as a replacement.
	* @param {boolean} isSingleRowMode - Flag indicating if single row mode is enabled.
	* @return {boolean} - Returns true if a replacement was made, false otherwise.
	*/
	replaceIfFits(targetLabel, newLabel, isSingleRowMode) {
		const { panel } = this.axis;
		const { left: paddingLeft, right: paddingRight } = panel.params.style.xAxis.label.padding;
		const availableWidth = panel.size.width - paddingLeft - paddingRight;
		const formatOptions = isSingleRowMode ? ['first', 'default'] : ['firstAlt', 'first', 'default'];

		for (const formatType of formatOptions) {
			const newFormatWidth = newLabel.width[formatType];

			if (newFormatWidth <= availableWidth) {
				targetLabel.format.default = newLabel.format[formatType];
				targetLabel.width.default = newFormatWidth;

				// Ensure the chosen format type is recorded correctly
				newLabel.format.default = newLabel.format[formatType];
				newLabel.width.default = newLabel.width[formatType];

				return true;
			}
		}

		return false;
	}

	/**
	 * Insert a label into the appropriate row that has alternative formatting (usually to ensure the year is always visible).
	 * Take "first" formatting from the top label's formats and insert a new label into the paired row label collection.
	 */
	updatePairedLabelRow(label, labels, targetRow) {

		// Get the paired label collection so we can add the "first"-styled label to it
		const pairedId = label.pairedFirst;
		let paired = this.core.params.xAxisSingleRowMode ? labels[targetRow] : labels[pairedId];

		if (!paired || !paired.length) {
			// There are no paired labels, create a new one
			const newLabel = deepClone(label);

			if (this.core.params.xAxisSingleRowMode && labels[targetRow]) {
				paired = labels[targetRow] = [newLabel];
			} else if (!this.core.params.xAxisSingleRowMode && labels[pairedId]) {
				paired = labels[pairedId] = [newLabel];
			}

			if (paired?.length) {
				paired[0].format.default = paired[0].format.first;
				paired[0].width.default = paired[0].width.first;
			}
		} else {
			// Insert label at the beginning of the collection if it precedes the first paired label
			if (paired[0].date - label.date > 0) {
				const newLabel = deepClone(label);
				paired.splice(0, 0, newLabel);
			}

			paired[0].format.default = label.format.first;
			paired[0].width.default = label.width.first;
			paired[0].x0 = label.x0;

			const { style } = this.axis.panel.params;
			const { left: paddingLeft, right: paddingRight } = style.xAxis.label.padding;
			const left = paired[0].x0 + paddingLeft - paddingRight;
			const right = left + paired[0].width.default;

			// CH-1615: Could we avoid this step by using the label priority?

			// Remove neighboring paired label(s) if the new label is now overlapping.
			// We do this here to ensure this label always shows up since the render loop
			// prioritizes right-hand labels.
			for (let x = paired.length - 1; x >= 1; x--) {
				if (paired[x].x0 && paired[x].x0 <= right) paired.splice(1, 1);
			}
		}

		return labels;
	}
}

export default Labeler;
